import React, { useEffect, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Card from '../../components/Card';
import Chart from 'react-apexcharts';
import { getOrdersRequest } from '../../requests/api';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fr as frLocale } from 'date-fns/locale';
registerLocale('fr', frLocale);

const Statistics = () => {
  const [orders, setOrders] = useState([]);
  const [chartData, setChartData] = useState({ categories: [], data: [] });
  const [viewMode, setViewMode] = useState('month'); // 'day', 'week', 'month'
  const [calcMode, setCalcMode] = useState('count'); // 'count', 'amount'
  const [loading, setLoading] = useState(true);
  // Par défaut: 6 derniers mois glissants
  const initialEnd = new Date();
  const initialStart = new Date();
  initialStart.setMonth(initialStart.getMonth() - 6);
  const [dateRange, setDateRange] = useState([initialStart, initialEnd]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    // Récupérer toutes les commandes (sans filtre de statut)
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const data = await getOrdersRequest({});
        setOrders(data);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error);
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  useEffect(() => {
    if (orders.length === 0) return;

    // Sélectionne la date de livraison/retrait en priorité
    const pickOrderDate = (order) => order.order_delivery_date || order.createdAt || order.date_created || null;
    const pickOrderAmount = (order) => {
      const amount = order.grand_total || order.total || order.total_amount || 0;
      return typeof amount === 'string' ? parseFloat(amount) : amount;
    };

    // Filtrer par plage de dates si définie
    const isInRange = (d) => {
      if (!startDate || !endDate) return true; // si la plage n'est pas complète, ne filtre pas
      const time = d.getTime();
      // Fin de journée pour inclure toutes les commandes du jour de fin
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      return time >= startDate.getTime() && time <= endOfDay.getTime();
    };

    // Grouper les commandes par date sélectionnée
    const groupedData = {};

    // Appliquer filtrage de plage avant agrégation
    const filteredOrders = orders.filter((order) => {
      const rawDate = pickOrderDate(order);
      if (!rawDate) return false;
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return false;
      return isInRange(d);
    });

    filteredOrders.forEach(order => {
      const rawDate = pickOrderDate(order);
      if (!rawDate) return;

      const date = new Date(rawDate);
      if (isNaN(date.getTime())) return;

      let key;

      if (viewMode === 'day') {
        // Format: YYYY-MM-DD
        key = date.toISOString().substr(0, 10);
      } else if (viewMode === 'week') {
        // Format: YYYY-Www (année-semaine)
        const year = date.getFullYear();
        const weekNumber = getWeekNumber(date);
        key = `${year}-S${weekNumber.toString().padStart(2, '0')}`;
      } else if (viewMode === 'month') {
        // Format: YYYY-MM
        key = date.toISOString().substr(0, 7);
      }

      if (!groupedData[key]) {
        groupedData[key] = 0;
      }
      
      if (calcMode === 'amount') {
        groupedData[key] += pickOrderAmount(order);
      } else {
        groupedData[key]++;
      }
    });

    // Trier les clés par ordre chronologique
    const sortedKeys = Object.keys(groupedData).sort();
    const sortedValues = sortedKeys.map(key => groupedData[key]);

    // Formater les labels selon le mode
    const formattedLabels = sortedKeys.map(key => {
      if (viewMode === 'day') {
        return new Date(key).toLocaleDateString('fr-FR');
      } else if (viewMode === 'week') {
        return key;
      } else if (viewMode === 'month') {
        const [year, month] = key.split('-');
        return `${month}/${year}`;
      }
      return key;
    });

    setChartData({
      categories: formattedLabels,
      data: sortedValues
    });
  }, [orders, viewMode, calcMode, startDate, endDate]);

  // Fonction pour calculer le numéro de semaine
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      toolbar: {
        show: true
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: -45,
        rotateAlways: chartData.categories.length > 10
      }
    },
    yaxis: {
      title: {
        text: calcMode === 'amount' ? 'Montant total (€)' : 'Nombre de commandes'
      },
      labels: {
        formatter: function (val) {
          return calcMode === 'amount' 
            ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val)
            : Math.floor(val);
        }
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return calcMode === 'amount' 
            ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val)
            : val + ' commande(s)';
        }
      }
    },
    colors: ['#3a57e8']
  };

  const series = [{
    name: calcMode === 'amount' ? 'Chiffre d\'affaires' : 'Commandes',
    data: chartData.data
  }];

  return (
    <>
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Statistiques des commandes</h4>
              <div className="d-flex align-items-center gap-2">
                <Form.Select 
                  style={{ width: 'auto' }}
                  value={calcMode}
                  onChange={(e) => setCalcMode(e.target.value)}
                >
                  <option value="count">Nombre de commandes</option>
                  <option value="amount">Montant des commandes</option>
                </Form.Select>
                <div className="me-2">
                  <DatePicker
                    selectsRange
                    locale="fr"
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => {
                      setDateRange(update);
                    }}
                    isClearable
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Plage de dates"
                    className="form-control"
                  />
                </div>
                <Form.Select 
                  style={{ width: 'auto' }}
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                >
                  <option value="day">Par jour</option>
                  <option value="week">Par semaine</option>
                  <option value="month">Par mois</option>
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <p>Chargement des données...</p>
              ) : orders.length === 0 ? (
                <p>Aucune commande disponible</p>
              ) : (
                <>
                  <p className="mb-3">
                    {calcMode === 'amount' ? 'Montant total: ' : 'Total de commandes: '} 
                    <strong>{(() => {
                      // recalcul rapide du total filtré pour l'info
                      const pickOrderDateLocal = (order) => order.order_delivery_date || order.createdAt || order.date_created || null;
                      const pickOrderAmountLocal = (order) => {
                        const amount = order.grand_total || order.total || order.total_amount || 0;
                        return typeof amount === 'string' ? parseFloat(amount) : amount;
                      };
                      const endOfDay = endDate ? new Date(new Date(endDate).setHours(23,59,59,999)) : null;
                      const filtered = orders.filter((o) => {
                        const rd = pickOrderDateLocal(o);
                        if (!rd) return false;
                        const d = new Date(rd);
                        if (isNaN(d.getTime())) return false;
                        if (startDate && endOfDay) {
                          return d.getTime() >= startDate.getTime() && d.getTime() <= endOfDay.getTime();
                        }
                        // si pas de plage complète sélectionnée, afficher 6 derniers mois par défaut
                        return true;
                      });
                      
                      if (calcMode === 'amount') {
                        const total = filtered.reduce((acc, curr) => acc + pickOrderAmountLocal(curr), 0);
                        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(total);
                      }
                      
                      return filtered.length;
                    })()}</strong>
                  </p>
                  <Chart
                    options={chartOptions}
                    series={series}
                    type="bar"
                    height={400}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Statistics;
